import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import theme from '../../styles/travelieTheme';

const TravelieLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setIsMobileSidebarOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isMenuActive = (children) => {
    return children.some(child => location.pathname === child.path);
  };

  // Navigation menu structure
  const menuItems = [
    { 
      id: 'overview', 
      label: 'Overview', 
      path: '/agent/dashboard',
      icon: '📊'
    },
    { 
      id: 'client-management', 
      label: 'Client Management', 
      icon: '👥',
      children: [
        { id: 'clients', label: 'Clients', path: '/agent/clients' },
        { id: 'workflow', label: 'Workflow Stages', path: '/agent/workflow' },
        { id: 'communications', label: 'Communications', path: '/agent/communications' },
        { id: 'workflow-analytics', label: 'Workflow Analytics', path: '/agent/workflow-analytics' }
      ]
    },
    { 
      id: 'tasks-projects', 
      label: 'Tasks & Projects', 
      icon: '📋',
      children: [
        { id: 'tasks', label: 'Tasks', path: '/agent/tasks' },
        ...(user.role === 'agency_admin' || user.role === 'super_admin' ? 
          [
            { id: 'task-templates', label: 'Task Templates', path: '/agent/task-templates' },
            { id: 'workflow-automation', label: 'Workflow Automation', path: '/agent/workflow-automation' }
          ] : 
          []
        )
      ]
    },
    { 
      id: 'business-ops', 
      label: 'Business Operations', 
      icon: '💼',
      children: [
        { id: 'leads', label: 'Leads', path: '/agent/leads' },
        { id: 'quotes', label: 'Quotes', path: '/agent/quotes' },
        { id: 'itineraries', label: 'Itineraries', path: '/agent/itineraries' },
        { id: 'commissions', label: 'Commissions', path: '/agent/commissions' },
        { id: 'payments', label: 'Payments', path: '/agent/payments' },
        { id: 'payment-reminders', label: 'Payment Reminders', path: '/agent/payment-reminders' },
        { id: 'payment-links', label: 'Payment Links', path: '/agent/payment-links' },
        { id: 'groups', label: 'Groups', path: '/agent/groups' }
      ]
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: '📈',
      children: [
        { id: 'analytics-dashboard', label: 'Analytics Dashboard', path: '/agent/analytics' },
        { id: 'payment-reports', label: 'Payment Reports', path: '/agent/payment-reports' }
      ]
    }
  ];

  // Sidebar styles
  const sidebarWidth = isSidebarCollapsed ? '80px' : theme.layout.sidebar.width;
  
  const sidebarStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: sidebarWidth,
    background: theme.colors.neutral.card,
    borderRight: `1px solid ${theme.colors.neutral.border}`,
    display: 'flex',
    flexDirection: 'column',
    transition: `width ${theme.transitions.normal}`,
    zIndex: 1000,
    overflowY: 'auto',
    overflowX: 'hidden'
  };

  const mobileSidebarStyle = {
    ...sidebarStyle,
    transform: isMobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: `transform ${theme.transitions.normal}`
  };

  const mainContentStyle = {
    marginLeft: isMobile ? '0' : sidebarWidth,
    minHeight: '100vh',
    background: theme.colors.neutral.background,
    transition: `margin-left ${theme.transitions.normal}`,
    padding: isMobile ? '0' : theme.spacing['3xl']
  };

  const logoStyle = {
    padding: theme.spacing['2xl'],
    borderBottom: `1px solid ${theme.colors.neutral.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: isSidebarCollapsed ? 'center' : 'space-between'
  };

  const menuItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    color: active ? theme.colors.primary.main : theme.colors.text.primary,
    background: active ? theme.colors.primary.light : 'transparent',
    borderLeft: active ? `3px solid ${theme.colors.primary.main}` : '3px solid transparent',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.regular,
    transition: `all ${theme.transitions.fast}`,
    gap: theme.spacing.md
  });

  const submenuItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    paddingLeft: theme.spacing['3xl'],
    color: active ? theme.colors.primary.main : theme.colors.text.secondary,
    background: active ? theme.colors.primary.light : 'transparent',
    cursor: 'pointer',
    textDecoration: 'none',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: active ? theme.typography.fontWeight.medium : theme.typography.fontWeight.regular,
    transition: `all ${theme.transitions.fast}`
  });

  const renderMenuItem = (item) => {
    if (item.children) {
      const isExpanded = expandedMenus[item.id];
      const hasActiveChild = isMenuActive(item.children);
      
      return (
        <div key={item.id}>
          <div
            style={menuItemStyle(hasActiveChild)}
            onClick={() => toggleMenu(item.id)}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.neutral.hover}
            onMouseLeave={(e) => e.currentTarget.style.background = hasActiveChild ? theme.colors.primary.light : 'transparent'}
          >
            <span style={{ fontSize: '18px' }}>{item.icon}</span>
            {!isSidebarCollapsed && (
              <>
                <span style={{ flex: 1 }}>{item.label}</span>
                <span style={{ 
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: `transform ${theme.transitions.fast}`
                }}>
                  ▼
                </span>
              </>
            )}
          </div>
          {isExpanded && !isSidebarCollapsed && (
            <div>
              {item.children.map(child => (
                <Link
                  key={child.id}
                  to={child.path}
                  style={submenuItemStyle(isActive(child.path))}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.neutral.hover}
                  onMouseLeave={(e) => e.currentTarget.style.background = isActive(child.path) ? theme.colors.primary.light : 'transparent'}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        style={menuItemStyle(isActive(item.path))}
        onMouseEnter={(e) => e.currentTarget.style.background = theme.colors.neutral.hover}
        onMouseLeave={(e) => e.currentTarget.style.background = isActive(item.path) ? theme.colors.primary.light : 'transparent'}
      >
        <span style={{ fontSize: '18px' }}>{item.icon}</span>
        {!isSidebarCollapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside style={{ ...sidebarStyle, display: isMobile ? 'none' : 'flex' }}>
        {/* Logo */}
        <div style={logoStyle}>
          {!isSidebarCollapsed && (
            <img 
              src="/Travel+ CRM Logo.png" 
              alt="Travel+ CRM Logo" 
              style={{
                height: '60px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          )}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              padding: theme.spacing.sm
            }}
          >
            {isSidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav style={{ flex: 1, paddingTop: theme.spacing.lg }}>
          {menuItems.map(item => renderMenuItem(item))}
        </nav>

        {/* User Profile Section */}
        <div style={{
          padding: theme.spacing.lg,
          borderTop: `1px solid ${theme.colors.neutral.border}`
        }}>
          {!isSidebarCollapsed && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.md,
                marginBottom: theme.spacing.md
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: theme.colors.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.colors.text.white,
                  fontWeight: theme.typography.fontWeight.bold
                }}>
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <p style={{
                    margin: 0,
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.medium,
                    color: theme.colors.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.text.secondary
                  }}>
                    {user?.role === 'agency_admin' ? 'Agency Admin' : 'Travel Agent'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  background: theme.colors.status.error,
                  color: theme.colors.text.white,
                  border: 'none',
                  borderRadius: theme.components.button.primary.borderRadius,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  cursor: 'pointer',
                  transition: `background ${theme.transitions.normal}`
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#DC2626'}
                onMouseLeave={(e) => e.currentTarget.style.background = theme.colors.status.error}
              >
                Logout
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Sidebar - Mobile */}
      <aside style={{ ...mobileSidebarStyle, display: isMobile ? 'flex' : 'none' }}>
        {/* Same content as desktop sidebar */}
        <div style={logoStyle}>
          <img 
            src="/Travel+ CRM Logo.png" 
            alt="Travel+ CRM Logo" 
            style={{
              height: '60px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '24px',
              padding: theme.spacing.sm
            }}
          >
            ✕
          </button>
        </div>

        <nav style={{ flex: 1, paddingTop: theme.spacing.lg }}>
          {menuItems.map(item => renderMenuItem(item))}
        </nav>

        <div style={{
          padding: theme.spacing.lg,
          borderTop: `1px solid ${theme.colors.neutral.border}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.md,
            marginBottom: theme.spacing.md
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: theme.colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.text.white,
              fontWeight: theme.typography.fontWeight.bold
            }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{
                margin: 0,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.text.primary
              }}>
                {user?.first_name} {user?.last_name}
              </p>
              <p style={{
                margin: 0,
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.text.secondary
              }}>
                {user?.role === 'agency_admin' ? 'Agency Admin' : 'Travel Agent'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              background: theme.colors.status.error,
              color: theme.colors.text.white,
              border: 'none',
              borderRadius: theme.components.button.primary.borderRadius,
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              cursor: 'pointer'
            }}
          >
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={mainContentStyle}>
        {/* Mobile Header */}
        <div style={{
          display: isMobile ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${theme.spacing.xl} ${theme.spacing.lg}`,
          background: '#07a5c3',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: theme.colors.text.white,
              border: 'none',
              padding: theme.spacing.md,
              borderRadius: theme.components.button.primary.borderRadius,
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            ☰
          </button>
          <img 
            src="/Travel+ CRM Logo Mobile.png" 
            alt="Travel+ CRM" 
            style={{
              height: '50px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <div style={{ width: '48px' }} /> {/* Spacer for centering */}
        </div>

        {/* Page Content */}
        <div style={{ padding: isMobile ? theme.spacing['2xl'] : '0' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default TravelieLayout;
